import { FullProg_v4 } from '~/providers/types';
import { DamWeekDayKey } from '~/providers/types/api-private';
import { groupWeekSchedules } from './schedules';

/** Geometria do gráfico ilustrativo de ventilação do design (viewBox 700 × 352, eixo de 0 a 24 h). */
export const VENT_CHART_X0 = 64;
export const VENT_CHART_X1 = 668;
const DAY_MINUTES = 1440;
/** Largura mínima legível das faixas de ventilação, que em minutos ficariam finas na escala de 24 h. */
const MIN_BAND_PX = 16;

/** Janela usada no design quando o DAM não tem programação que permita refrigeração. */
export const DEFAULT_VENTILATION_WINDOW = { start: '08:00', end: '18:00' };

export interface VentilationWindow {
  start: string;
  end: string;
}

export interface VentilationChartGeometry {
  vent1X: number;
  vent1W: number;
  vent2X: number;
  vent2W: number;
  progX1: number;
  progX2: number;
}

export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

export function addMinutes(time: string, delta: number): string {
  const total = (((toMinutes(time) + delta) % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Programação que ilustra o gráfico: a de hoje que permite refrigeração, senão a primeira que permite. */
export function pickVentilationWindow(week: FullProg_v4['week'], today: DamWeekDayKey): VentilationWindow {
  const allowGroups = groupWeekSchedules(week).filter((group) => group.permission === 'allow');
  const group = allowGroups.find((item) => item.days.includes(today)) ?? allowGroups[0];
  return group ? { start: group.start, end: group.end } : DEFAULT_VENTILATION_WINDOW;
}

function xForMinute(minute: number): number {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, minute));
  return VENT_CHART_X0 + (clamped / DAY_MINUTES) * (VENT_CHART_X1 - VENT_CHART_X0);
}

/** Mesma conta do design: faixas no início e no fim da programação, com a duração da ventilação. */
export function computeVentilationChart(window: VentilationWindow, ventStart: number, ventEnd: number): VentilationChartGeometry {
  const startMin = toMinutes(window.start);
  const endRaw = toMinutes(window.end);
  const endMin = Math.min(endRaw > startMin ? endRaw : endRaw + DAY_MINUTES, DAY_MINUTES);
  const vent1X = xForMinute(startMin);
  const vent1W = Math.max(xForMinute(startMin + ventStart) - vent1X, MIN_BAND_PX);
  const progX2 = xForMinute(endMin);
  const vent2W = Math.max(progX2 - xForMinute(endMin - ventEnd), MIN_BAND_PX);
  return {
    vent1X, vent1W, vent2X: progX2 - vent2W, vent2W, progX1: vent1X, progX2,
  };
}
